/** Largest page trip-manager returns for one `tripAnnotations` call. */
export const ANNOTATION_PAGE_SIZE = 500

/**
 * Most pages `fetchAllPages` requests, the first included: 50,000 rows at the
 * page size above. Well past INT's 41,498-row backfill, and a stop for a list
 * that never ends. It bounds requests, not rows, so overlapping pages cannot
 * stretch it.
 */
export const MAX_ANNOTATION_PAGES = 100

/**
 * Most pages a map layer loads: 10,000 notes. A map holds every row for as
 * long as it is open, and only the notes near the camera become markers, so
 * this bounds memory rather than drawing cost. A metro with more than this
 * shows its newest 10,000 and says so in the Annotations menu.
 */
export const MAX_ANNOTATION_MAP_PAGES = 20

/**
 * How many pages {@link loadAnnotationPages} asks for at once. Enough to cut a
 * long history's wait several times over, few enough that trip-manager, whose
 * deep `OFFSET`s get slower the further back they read, is not flooded.
 */
export const ANNOTATION_PAGE_CONCURRENCY = 4

export type AnnotationPagesResult<T> = {
  /** Every row loaded, the ones passed in first, without duplicates. */
  rows: T[]
  /** The list ended. `false` when loading stopped early (cap, error, a backend ignoring `offset`). */
  complete: boolean
  /** Why loading stopped early, when a page failed. */
  error?: unknown
}

/**
 * Loads the pages after the rows already `loaded`, `concurrency` pages at a
 * time, and hands back the whole list once, rather than one page at a time.
 *
 * Paging is by offset over a newest-first list, `pageSize` rows a page. It
 * starts at the page after the last full one in `loaded`, so a list restored
 * from the cache resumes where it ended instead of starting over. Pages are
 * merged in offset order, and loading stops at the first page that:
 *
 * - is short: the list ended (`complete`);
 * - is full but adds nothing new: a backend that ignores `offset`, which would
 *   otherwise hand back the same page forever;
 * - failed: the rows before it are kept, and `error` says why.
 *
 * It also stops after `maxPages` pages, `loaded` counted. Requests already in
 * flight past a short page are still awaited, but their rows are dropped.
 *
 * Returns `null` once `isCancelled` says so, so a caller whose query changed
 * mid-load can drop the result.
 */
export const loadAnnotationPages = async <T extends { annotationId: string }>({
  loaded,
  fetchPage,
  maxPages = MAX_ANNOTATION_PAGES,
  concurrency = ANNOTATION_PAGE_CONCURRENCY,
  pageSize = ANNOTATION_PAGE_SIZE,
  isCancelled = () => false,
  onBatch,
}: {
  loaded: readonly T[]
  fetchPage: (offset: number) => Promise<readonly T[]>
  maxPages?: number
  concurrency?: number
  pageSize?: number
  isCancelled?: () => boolean
  /**
   * Called after each batch of pages that leaves more to load, with every row
   * loaded so far, so a caller can show the list growing. The final batch is
   * not reported here; it is the resolved result.
   */
  onBatch?: (rows: readonly T[]) => void
}): Promise<AnnotationPagesResult<T> | null> => {
  const rows = [...loaded]
  const seen = new Set(rows.map(row => row.annotationId))
  if (rows.length < pageSize) return { rows, complete: true }

  let nextPage = Math.floor(rows.length / pageSize)
  while (nextPage < maxPages) {
    const batch = Array.from(
      { length: Math.min(concurrency, maxPages - nextPage) },
      (_, index) => (nextPage + index) * pageSize,
    )
    nextPage += batch.length
    const pages = await Promise.allSettled(batch.map(fetchPage))
    if (isCancelled()) return null
    for (const page of pages) {
      if (page.status === 'rejected') {
        return { rows, complete: false, error: page.reason }
      }
      let added = 0
      for (const row of page.value) {
        if (seen.has(row.annotationId)) continue
        seen.add(row.annotationId)
        rows.push(row)
        added += 1
      }
      if (page.value.length < pageSize) return { rows, complete: true }
      if (added === 0) return { rows, complete: false }
    }
    if (nextPage < maxPages) onBatch?.([...rows])
  }
  return { rows, complete: false }
}

/**
 * Appends a newer-to-older page onto the rows already loaded, dropping any row
 * that is already there.
 *
 * Paging is by offset over a newest-first list, so an annotation recorded
 * while the pages are loading shifts every later row down by one and the next
 * page repeats the last row of the previous one. Without the drop, that row
 * would be counted twice in the table and the weekly chart.
 */
export const appendAnnotationPage = <T extends { annotationId: string }>(
  loaded: readonly T[],
  page: readonly T[],
): T[] => {
  const seen = new Set(loaded.map(row => row.annotationId))
  return [...loaded, ...page.filter(row => !seen.has(row.annotationId))]
}

export type AnnotationListSignature = {
  count: number
  firstId: string | undefined
  lastId: string | undefined
}

export const annotationListSignature = (
  rows: readonly { annotationId: string }[],
): AnnotationListSignature => ({
  count: rows.length,
  firstId: rows[0]?.annotationId,
  lastId: rows.at(-1)?.annotationId,
})

/**
 * Whether `rows` is the list `previous` described, possibly with more pages
 * appended, rather than a fresh base page.
 *
 * Appending pages keeps the first row and the row at the old end where they
 * were. A refetch swaps in the newest page alone, so an annotation recorded in
 * between moves the first row or pushes the old last row out of the window, and
 * a shorter list means the older pages are gone. Comparing both ends catches a
 * refetch that returns the same number of rows, which a count alone cannot.
 */
export const isSameAnnotationList = (
  previous: AnnotationListSignature,
  rows: readonly { annotationId: string }[],
): boolean =>
  rows.length >= previous.count &&
  rows[0]?.annotationId === previous.firstId &&
  rows[previous.count - 1]?.annotationId === previous.lastId
